import React from 'react';
import { useTranslation } from 'react-i18next';
import { User, Calendar } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shared/ui';
import type { CurriculumItem } from '@/data/classes';
import type { Participant } from '@/data/participants';

export interface SessionAuthUser {
  userId: number;
  name?: string;
  currentAccount?: { organizationName?: string };
}

interface SessionHeaderProps {
  sessionItem: CurriculumItem;
  isAllParticipantsView: boolean;
  selectedParticipant: Participant | null;
  authUser: SessionAuthUser | null | undefined;
  sortCriteria: string;
  completionDates: string[];
  selectedCompletionDate: string;
  onCompletionDateChange: (v: string) => void;
}

/** 시나리오명·참가자·완료일 */
export default function SessionHeader({
  sessionItem,
  isAllParticipantsView,
  selectedParticipant,
  authUser,
  sortCriteria,
  completionDates,
  selectedCompletionDate,
  onCompletionDateChange,
}: SessionHeaderProps): React.ReactElement {
  const { t } = useTranslation('class');
  return (
    <div className="flex items-end justify-between border-b border-slate-200/80 pb-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900">{sessionItem.name}</h2>
          {sessionItem?.practiceMode && (
            <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-50 text-blue-600 border border-blue-100">
              {sessionItem.practiceMode}
            </span>
          )}
        </div>
        <div className="text-base flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <User size={16} className="text-gray-400" />
            <span className="font-semibold">
              {isAllParticipantsView ? t('shared.allParticipants') : selectedParticipant?.name || authUser?.name}
            </span>
          </div>
          {!isAllParticipantsView && (
            <>
              <span className="mx-2 text-gray-300">|</span>
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-gray-400" />
                <span>{t('simulationResults.completionDate')}</span>
                {sortCriteria === 'all' ? (
                  <Select value={selectedCompletionDate} onValueChange={onCompletionDateChange}>
                    <SelectTrigger className="w-[180px] h-8 text-base font-bold bg-white border-gray-200 -ml-1 focus:ring-1">
                      <SelectValue placeholder={t('simulationResults.completionDatePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {completionDates.map((date) => (
                        <SelectItem key={date} value={date} className="text-base font-medium">
                          {date}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="font-medium text-gray-700">{selectedCompletionDate}</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
